import { BusinessMember } from "../types";
import { SUPPORT_EMAIL } from "../constants";

/**
 * ENG-3341 — an owner cannot delete their account while their organization has other members.
 *
 * Deleting the account of an organization's owner deleted the whole organization: its subscription
 * was cancelled and every member was removed from it, behind a Danger Zone that said only "delete
 * your account". The backend now refuses (409) when the caller owns an org anyone else is active in;
 * this module is the console's half — saying so BEFORE the person starts, from what the console
 * already knows, and saying the same thing when the server refuses anyway.
 *
 * Pure, like `orgMembership` and `billingAccess`, so each state is assertable without a render.
 */

/** One organization blocking the deletion. Mirrors the backend's `OwnedOrganizationResponse`. */
export interface OwnedOrgWithMembers {
  tenantId: string;
  name: string;
  /** Every other ACTIVE membership, outside administrators included. Always at least 1. */
  otherMemberCount: number;
  /**
   * A family's ownership moves only through Haven support (ENG-3246), so its owner is not sent to
   * Members & seats to hand it over. From the roster of the org the console is showing, or (ENG-3897)
   * the server's 409, which says it since then; absent from a backend older than that.
   */
  tenantType?: string | null;
}

/** The backend's machine-readable reason (`AccountDeletionRefusedResponse.CODE`). Keep in step. */
export const OWNS_ORGANIZATION_WITH_MEMBERS = "owns_organization_with_members";

/** The org a `GET /tenant/members` roster describes, as that response names it. */
export interface RosterOrg {
  tenantId: string;
  tenantName: string;
}

/**
 * The org the console can already see blocking a deletion, or null.
 *
 * Read off the roster (`GET /tenant/members`), not `/subscription-status`, because the roster is
 * the one source carrying both halves the server checks: the FLAT `role` it tests for "owner", and
 * every membership with its active flag. The two responses can also describe different orgs for
 * someone in several — the roster names its own org, so the name here is always the org counted.
 *
 * Deliberately partial. It only knows the org the console is showing, so an owner who has switched
 * to another org, or whose roster has not loaded, gets null here and the server's 409 instead — the
 * same message, one click later. It never GRANTS anything: a null here does not mean the server
 * will allow the deletion.
 */
export function ownedOrgBlockingDeletion(
  rosterOrg: RosterOrg | null,
  members: BusinessMember[],
  membersLoaded: boolean,
  selfEmail: string | null | undefined,
  rosterTenantType?: string | null,
): OwnedOrgWithMembers | null {
  if (!membersLoaded || !rosterOrg || !selfEmail) return null;
  const me = selfEmail.trim().toLowerCase();
  const mine = members.find((m) => m.email?.trim().toLowerCase() === me);
  if (!mine || mine.role !== "owner") return null;
  // Management-only rows count: an outside administrator is still removed from the org when it is
  // deleted. Inactive (removed) rows do not — the same predicate as the server's.
  const others = members.filter((m) => m.isActive && m.id !== mine.id).length;
  return others > 0
    ? { tenantId: rosterOrg.tenantId, name: rosterOrg.tenantName, otherMemberCount: others, ...(rosterTenantType ? { tenantType: rosterTenantType } : {}) }
    : null;
}

/**
 * The organizations in a `DELETE /account` 409 body, or null when the body is not that refusal.
 *
 * Matched on the `code`, not the status: any other conflict falls through to the plain error path,
 * rather than rendering a list of organizations out of a body that has none.
 */
export function parseDeletionRefusal(body: unknown): OwnedOrgWithMembers[] | null {
  const data = (body ?? {}) as { code?: unknown; organizations?: unknown };
  if (data.code !== OWNS_ORGANIZATION_WITH_MEMBERS || !Array.isArray(data.organizations)) return null;
  const orgs = data.organizations.flatMap((o: unknown): OwnedOrgWithMembers[] => {
    const org = (o ?? {}) as { tenantId?: unknown; name?: unknown; otherMemberCount?: unknown; tenantType?: unknown };
    return typeof org.name === "string" && typeof org.otherMemberCount === "number"
      ? [{
          tenantId: typeof org.tenantId === "string" ? org.tenantId : "",
          name: org.name,
          otherMemberCount: org.otherMemberCount,
          ...(typeof org.tenantType === "string" ? { tenantType: org.tenantType } : {}),
        }]
      : [];
  });
  return orgs.length > 0 ? orgs : null;
}

/**
 * ENG-3493 — the server's own sentence when its SECOND owner check refused, or null for any other body.
 *
 * The server checks again under a lock, after it has cancelled the subscriptions and deleted the
 * sign-in, and can refuse there when someone joined meanwhile. That 409 carries the same code and
 * organizations as the first check's, plus `cancelledOrganizations`, which the first never has. Its
 * `error` says what had already happened (signed out within the hour, which subscriptions stay
 * cancelled), which the Danger Zone's own sentence (`describeDeletionBlock`) cannot know, so the
 * console shows it too.
 */
export function lateRefusalNotice(body: unknown): string | null {
  const data = (body ?? {}) as { code?: unknown; cancelledOrganizations?: unknown; error?: unknown };
  if (data.code !== OWNS_ORGANIZATION_WITH_MEMBERS || !Array.isArray(data.cancelledOrganizations)) return null;
  return typeof data.error === "string" && data.error.trim() ? data.error : null;
}

/**
 * What to tell the owner. The same sentence the backend puts in the 409's `error`
 * (`accountDeletionRefusalMessage` in TenantAuth.kt), so a client reading only that says the same.
 * "Members & seats" is the page's own label (`ADMIN_PAGE_LABELS.users`).
 */
export function describeDeletionBlock(orgs: OwnedOrgWithMembers[]): string {
  const members = (n: number) => (n === 1 ? "1 other member" : `${n} other members`);
  if (orgs.length === 1) {
    const [org] = orgs;
    // Consistency audit, 2026-10-15: a family cannot be handed over in the console (ENG-3246).
    if (org.tenantType === "family") {
      return `You own ${org.name}, which has ${members(org.otherMemberCount)}. ` +
        `To hand the family to one of them, email ${SUPPORT_EMAIL} before deleting your account.`;
    }
    return `You own ${org.name}, which has ${members(org.otherMemberCount)}. ` +
      "Make someone else the owner in Members & seats before deleting your account.";
  }
  const each = orgs.map((o) => `${o.name} (${members(o.otherMemberCount)})`);
  const list = `${each.slice(0, -1).join(", ")} and ${each[each.length - 1]}`;
  // ENG-3897: a family among them is handed over by support, as one alone is.
  const families = orgs.filter((o) => o.tenantType === "family").length;
  if (families === orgs.length) {
    return `You own ${list}. To hand each family to one of its members, email ${SUPPORT_EMAIL} before deleting your account.`;
  }
  if (families > 0) {
    return `You own ${list}. Before deleting your account, make someone else the owner of each organization in ` +
      `Members & seats, and email ${SUPPORT_EMAIL} to hand each family to one of its members.`;
  }
  return `You own ${list}. Make someone else the owner of each in Members & seats before deleting your account.`;
}

/** ENG-3897: Members & seats can hand over none of them, since each is a family. */
export const onlyFamiliesBlock = (orgs: OwnedOrgWithMembers[]): boolean =>
  orgs.length > 0 && orgs.every((o) => o.tenantType === "family");

/**
 * ENG-3369 — which org "Go to Members & seats" must open for an owner the deletion is blocked by.
 *
 * The button used to open Members & seats for whichever org the console was showing. A multi-org
 * owner blocked by org A while looking at org B landed on B's roster, where there was nothing to fix.
 * Returns the tenant to switch to first, or null to open the page as it is: the blocking org is
 * already the one shown, or it is not one this person can switch to (the sentence still names it).
 * With several blocking orgs it takes the first, in the server's membership order.
 */
export function tenantToOpenForBlock(
  orgs: OwnedOrgWithMembers[],
  activeTenantId: string | null,
  switchableTenantIds: string[],
): string | null {
  const target = orgs[0]?.tenantId;
  if (!target || target === activeTenantId) return null;
  return switchableTenantIds.includes(target) ? target : null;
}

/**
 * What `DELETE /account` reported once it had deleted the account — the receipt the
 * account-deleted page is worded from.
 */
export interface DeletionReceipt {
  plan: string;
  currentPeriodEnd: number | null;
  /**
   * ENG-3369: a live paid subscription was cancelled, so "you won't be charged again" is true. The
   * server now says so explicitly; a backend that predates the field is read the way this page always
   * read it, from `plan`.
   */
  subscriptionCancelled: boolean;
  /**
   * ENG-3405: the server deleted the Firebase user, so every other device is signed out too (within
   * the hour its current ID token lives). False from a backend that predates it.
   */
  signedOutEverywhere: boolean;
}

/** The receipt in a `DELETE /account` 200 body. Tolerates the fields an older backend leaves out. */
export function parseDeletionReceipt(body: unknown): DeletionReceipt {
  const data = (body ?? {}) as { plan?: unknown; currentPeriodEnd?: unknown; subscriptionCancelled?: unknown; firebaseUserDeleted?: unknown };
  const plan = typeof data.plan === "string" ? data.plan : "free";
  return {
    plan,
    currentPeriodEnd: typeof data.currentPeriodEnd === "number" ? data.currentPeriodEnd : null,
    subscriptionCancelled: typeof data.subscriptionCancelled === "boolean" ? data.subscriptionCancelled : plan !== "free",
    signedOutEverywhere: data.firebaseUserDeleted === true,
  };
}

/**
 * ENG-3369: whether a `DELETE /account` that stopped part-way (502/503/500 with a `code`) had already
 * cancelled a subscription. Those cancellations stand, so a retry that then succeeds finds nothing
 * left to cancel and reports `subscriptionCancelled: false`; the console carries this forward so the
 * receipt still says the person won't be charged again.
 */
export function cancelledBeforeStopping(body: unknown): boolean {
  const orgs = (body as { cancelledOrganizations?: unknown } | null)?.cancelledOrganizations;
  return Array.isArray(orgs) && orgs.length > 0;
}
